/**
 * notification-preferences.ts
 * A user's own choice per informational notification type
 * (odd/tasks/notification-tasks.md, S9): GET/PUT /me/notification-preferences.
 * Only the types the user changed are stored and returned; a missing type means
 * on, so the frontend merges these with its catalog of labels. inApp = false
 * stops informational notifications of that type for the user; pending tasks
 * are never filtered (the mandatory floor). email = false leaves the type out
 * of the user's mail digest (S11), whose frequency is also chosen here; pending
 * tasks always go in the digest.
 */
import { z } from 'zod';

export const MAX_NOTIFICATION_PREFERENCE_ITEMS = 100;

/** "module:name" or "module.name" — the shape of every notification `type`. */
export const NOTIFICATION_TYPE_PATTERN = /^[a-z]+[:.][a-z0-9.:-]+$/;

/**
 * The approval TASK types (notification tasks, S3): the ones emitters write with
 * a taskKey. They are the mandatory floor of the preferences (S9): never muted,
 * shown as "Siempre". A new approval task type is added here, next to its emitter.
 */
export const TASK_NOTIFICATION_TYPES: readonly string[] = [
  'compras:case-approval-pending',
  'compras:po-approval-n1',
  'compras:po-approval-n2',
  'nomi.ausencia.solicitada',
  'nomi:recruitment-requested',
];

/** How often a user gets the mail digest (S11). 'weekly' goes out on Mondays. */
export const DIGEST_FREQUENCIES = ['daily', 'weekly', 'never'] as const;
export const DigestFrequencySchema = z.enum(DIGEST_FREQUENCIES);
export type DigestFrequency = z.infer<typeof DigestFrequencySchema>;
/** A user who never chose one. */
export const DEFAULT_DIGEST_FREQUENCY: DigestFrequency = 'daily';

const NotificationTypeSchema = z.string().regex(NOTIFICATION_TYPE_PATTERN, 'Tipo de notificación inválido.');

export const NotificationPreferenceSchema = z.object({
  type: NotificationTypeSchema,
  inApp: z.boolean(),
  email: z.boolean(),
});
export type NotificationPreference = z.infer<typeof NotificationPreferenceSchema>;

/** One type to change: a channel left out keeps its stored value (on when never stored). */
export const NotificationPreferenceChangeSchema = z
  .object({ type: NotificationTypeSchema, inApp: z.boolean().optional(), email: z.boolean().optional() })
  .refine((p) => p.inApp !== undefined || p.email !== undefined, { message: 'Indicá qué canal cambia.', path: ['type'] });
export type NotificationPreferenceChange = z.infer<typeof NotificationPreferenceChangeSchema>;

/**
 * Upserts each listed type; types not listed keep their stored value. Approval
 * task types are the mandatory floor: they can never be stored, not even
 * through a direct API call.
 */
export const PutNotificationPreferencesBodySchema = z.object({
  items: z
    .array(
      NotificationPreferenceChangeSchema.refine((p) => !TASK_NOTIFICATION_TYPES.includes(p.type), {
        message: 'Las aprobaciones no se pueden desactivar.',
        path: ['type'],
      }),
    )
    .max(MAX_NOTIFICATION_PREFERENCE_ITEMS, `Como máximo ${MAX_NOTIFICATION_PREFERENCE_ITEMS} preferencias por vez.`),
  /** Omitted: the stored frequency is kept. */
  digestFrequency: DigestFrequencySchema.optional(),
});
export type PutNotificationPreferencesBody = z.infer<typeof PutNotificationPreferencesBodySchema>;

/** The stored preferences of the user, by type, and their digest frequency. */
export const NotificationPreferencesResponseSchema = z.object({
  items: z.array(NotificationPreferenceSchema),
  digestFrequency: DigestFrequencySchema,
});
export type NotificationPreferencesResponse = z.infer<typeof NotificationPreferencesResponseSchema>;
